import React, { useEffect, useState } from "react";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import { LoadingSpinner } from "../Components/LoadingSpinner";

/**
 * /ouvrir?url=<chemin public sans préfixe de langue>&lang=fr : le convertisseur
 * automatique du widget de préversion. Le payload baké d'une page ne porte pas
 * toujours un slug admin fiable : le widget envoie le chemin public sur lequel il
 * se trouve et l'admin résout ici l'éditeur cible via la source SQL
 * (GET /api/content/resolve-editor), puis redirige :
 *   - page   → /page/{id}
 *   - record → /content/{schemaKey}/{id}
 * Passe par l'instance axios partagée (Auth0AxiosBridge) : un fetch brut serait
 * silencieusement 401 sur les instances déployées.
 */
export default function OuvrirDansAdmin(): React.ReactElement {
	const history = useHistory();
	const location = useLocation();
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const path = (params.get("url") ?? "").trim();
		const lang = (params.get("lang") ?? "fr").trim().toLowerCase();
		if (!path) {
			history.replace("/pages");
			return;
		}
		let cancelled = false;
		void (async () => {
			try {
				const res = await axios.get("/api/content/resolve-editor", { params: { path, language: lang } });
				if (cancelled) return;
				const t = res.data ?? {};
				if (t.kind === "page" && t.id) {
					history.replace(`/page/${encodeURIComponent(t.id)}`);
				} else if (t.id && t.schemaKey) {
					history.replace(`/content/${encodeURIComponent(t.schemaKey)}/${encodeURIComponent(t.id)}`);
				} else {
					setError(`Aucun contenu ne correspond à « ${path} ».`);
				}
			} catch {
				if (!cancelled) setError(`Aucun contenu ne correspond à « ${path} ».`);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [location.search, history]);

	if (error) {
		return (
			<div className="min-h-[50vh] flex items-center justify-center px-4">
				<div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
					<p className="text-sm font-semibold text-slate-800">Contenu introuvable</p>
					<p className="mt-2 text-sm text-slate-500">{error}</p>
					<button
						type="button"
						onClick={() => history.replace("/pages")}
						className="mt-4 rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
					>
						Parcourir les pages
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-[50vh] flex flex-col items-center justify-center gap-3">
			<LoadingSpinner size="lg" />
			<p className="text-sm text-slate-500">Résolution de la page…</p>
		</div>
	);
}
